import { createApp } from 'vue';

// Vuelidate validation library https://vuelidate.js.org/
import VueApexCharts from 'vue3-apexcharts';

import { datadogRum } from '@datadog/browser-rum';
import VueClipboard from 'vue-clipboard3';
import Filters from '@/filters.js';
import App from './App.vue';
import './registerServiceWorker';
import router from './router/index.js';
import store from './store/index.js';
import vuetify from './plugins/vuetify.js';
import axiosInstance from './plugins/axios.js';

import '@fontsource/roboto'; // Defaults to weight 400.

if (import.meta.env.VITE_APP_DATADOG_APPLICATION_ID && import.meta.env.VITE_APP_DATADOG_CLIENT_TOKEN) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_APP_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_APP_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'admin-portal',
    env: import.meta.env.MODE,
    version: import.meta.env.VITE_APP_VERSION,
    sampleRate: 100,
    trackInteractions: true,
    trackFrustrations: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

datadogRum.startSessionReplayRecording();

// Configure axios interceptors (must be before app creation so store modules can use it)
// Axios request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const c = { ...config };
    c.headers['X-ROUTE-CONTEXT'] = btoa(
      JSON.stringify({
        name: router.currentRoute.value.name,
        params: router.currentRoute.value.params,
      }),
    );
    return c;
  },
  (error) => Promise.reject(error),
);

// Axios response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // log user out if 401 or 419 is returned (or 503 for maintenance mode)
    if (error.response && [419, 401, 503].includes(error.response.status)) {
      const isLoginPage = router.currentRoute.value.name === 'login';

      if ([419, 401].includes(error.response.status)) {
        // If on login page, don't attempt logout (which would cause another 419)
        // Just let the error propagate so login page can handle it
        if (!isLoginPage && store.state.currentUser.isLoggedIn) {
          axiosInstance
            .get('/sanctum/csrf-cookie')
            .then(() => store.dispatch('currentUser/logUserOut'))
            .then(() => {
              router.push({ name: 'login' });
            });
        }
      } else if (error.response.status === 503) {
        if (store.state.currentUser.isLoggedIn) {
          store.dispatch('currentUser/logUserOut').then(() => {
            router.push({ name: 'Maintenance' });
          });
        } else {
          router.push({ name: 'Maintenance' });
        }
      }
    }

    return Promise.reject(error);
  },
);

// Create Vue app instance
const app = createApp(App);

// Use plugins
app.use(router);
app.use(store);
app.use(vuetify);
app.use(VueApexCharts);
app.use(Filters);
app.use(VueClipboard);

// Register global components
app.component('Apexchart', VueApexCharts);

// Add axios to global properties
app.config.globalProperties.$axios = axiosInstance;

// Add global mixin
app.mixin({
  computed: {
    isProd() {
      return import.meta.env.MODE === 'production';
    },
  },
  methods: {
    hasPermission(permission) {
      const permissions = store.state.currentUser.user.permission;

      // defensive check: ensure permissions exist and is an array
      if (!permissions || !Array.isArray(permissions)) {
        return false;
      }

      for (let i = 0; i < permissions.length; i += 1) {
        if (permissions[i].name === permission) {
          return true;
        }
      }
      return false;
    },
  },
});

// Mount the app
app.mount('#app');
